/**
 * What the HR screens share: exact money (pay is not rounded to "12k"), the
 * sample banner, the answer given to somebody without hr:read, and labels.
 *
 * The tiles, coverage lines and charts are Asset Management's, so the two
 * suites that price the estate read alike.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { Card, ErrorState } from "../../design/components";
import { TRADE_LABEL, type Trade } from "../../domain/hr";
import { t } from "../../i18n";
import { s } from "../assetManagement/shared";

export { ColumnChart, Coverage, Tiles, money, s } from "../assetManagement/shared";

export const SUITE = "Human Resources";

/** SAR 12,450 — whole riyals, for pay and cost. */
export function sar(v: number | null | undefined, currency = "SAR"): string {
  if (v == null) return "—";
  return `${currency} ${Math.round(v).toLocaleString("en")}`;
}

/** SAR 92.52 — two places, for an hourly rate. */
export function rate(v: number | null | undefined, currency = "SAR"): string {
  if (v == null) return "—";
  return `${currency} ${v.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function tradeLabel(tr: string | null | undefined): string {
  if (!tr || tr === "—") return "—";
  return t(TRADE_LABEL[tr as Trade] ?? tr);
}

export function pct(v: number | null | undefined, digits = 1): string {
  return v == null ? "—" : `${v.toFixed(digits)}%`;
}

/** Said on every HR screen of the demonstration: these are not Madinah's people. */
export function SampleBanner() {
  const { source } = useSession();
  if (source.kind !== "mock") return null;
  return (
    <div className={s.coverage} data-testid="hr-sample">
      <div>
        <b>{t("Sample workforce.")}</b>{" "}
        {t(
          "Demo people and pay, not the airport's staff. On the live platform, HR fills the register by hand or from payroll.",
        )}
      </div>
    </div>
  );
}

/** The platform said no (403), as opposed to failing. */
export function refused(error: unknown): boolean {
  if ((error as { status?: number } | null)?.status === 403) return true;
  const msg = error instanceof Error ? error.message : String(error);
  return /^Not permitted|\bhr:read\b/.test(msg);
}

/** A refusal is an answer: say who can read pay, rather than print "403". */
export function HrError({ error, retry }: { error: unknown; retry?: () => void }) {
  if (refused(error))
    return (
      <Card title={t("Pay is personal data")}>
        <p className={s.note}>
          {t(
            "Your role can't view HR data. An administrator can grant access on the Administration screen.",
          )}{" "}
          <Link to="/administration">{t("Administration")}</Link>
        </p>
      </Card>
    );
  return <ErrorState error={error} retry={retry} />;
}

/** The rules' provenance, said once under every figure that rests on them. */
export function RulesBasis({ basis }: { basis: "default" | "entered" }) {
  return (
    <p className={s.note}>
      {basis === "default"
        ? t("Rates: published defaults, proposed until HR confirms them.")
        : t("Rates: as set by this airport on the rules screen.")}{" "}
      <Link to="/hr-rules">{t("Saudi labour rules")}</Link>
    </p>
  );
}

export function Err({ text }: { text?: string }) {
  return text ? <span className={s.err}>{text}</span> : null;
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className={s.fieldset}>
      <legend>{title}</legend>
      <div className={s.form}>{children}</div>
    </fieldset>
  );
}
